/**
 * TanStack Router integration for Datadog RUM and Pendo
 * Manually tracks route changes since TanStack Router is not natively supported
 */

import { datadogRum } from "@datadog/browser-rum";
import type { AnyRouter } from "@tanstack/react-router";
import { logger } from "./logger";

declare const pendo: any;

let routerUnsubscribe: (() => void) | null = null;

/**
 * Initialize route tracking for TanStack Router
 * Call this after the router is created
 */
export function initializeRouteTracking(router: AnyRouter): void {
  if (routerUnsubscribe) {
    logger.warn("Route tracking already initialized");
    return;
  }

  // Track initial route on mount
  trackCurrentRoute(router);

  // Subscribe to route changes
  routerUnsubscribe = router.subscribe("onLoad", () => {
    trackCurrentRoute(router);
    //inform pendo when a page is loading.
    if (typeof pendo !== "undefined") pendo.pageLoad();
  });

  logger.info("Route tracking initialized for TanStack Router");
}

/**
 * Stop route tracking (cleanup)
 */
export function stopRouteTracking(): void {
  if (routerUnsubscribe) {
    routerUnsubscribe();
    routerUnsubscribe = null;
    logger.info("Route tracking stopped");
  }
}

/**
 * Track the current route in Datadog RUM
 */
function trackCurrentRoute(router: AnyRouter): void {
  try {
    const state = router.state;
    const location = state.location;

    // Get the current route path
    const pathname = location.pathname;

    // Get route matches to build a more descriptive name
    const matches = state.matches;
    const routeId = matches.length > 0 ? matches[matches.length - 1]?.routeId : pathname;

    // Create a view name - use routeId if available, otherwise pathname
    const viewName = routeId || pathname || "/";

    // Start a new view in Datadog
    datadogRum.startView({
      name: viewName,
    });

    logger.debug(`Route tracked: ${viewName}`);
  } catch (error) {
    logger.error("Failed to track route:", error);
  }
}
